import { questionText } from './question';
import type {
  DiagnosisInput,
  DiagnosisProvider,
  DiagnosisResult,
  ExplanationInput,
  ExplanationResult
} from './provider';

export class RuleProvider implements DiagnosisProvider {
  async diagnose(input: DiagnosisInput): Promise<DiagnosisResult> {
    const percent = Math.round((input.score / Math.max(1, input.questionCount)) * 100);
    const subject = input.childName?.trim() ? input.childName.trim() : 'You';

    // A flawless run should be celebrated, not handed a "weakness". Analytics still
    // flags the relatively-slowest correct facts so the follow-up has something to
    // practise, but the coach note and focus area stay positive.
    if (input.questionCount > 0 && input.score === input.questionCount) {
      return {
        diagnosis: `${subject} answered all ${input.questionCount} correctly — a perfect ${percent}%! That's total recall. See if you can beat your time on the next run.`,
        focusArea: 'Total recall'
      };
    }

    const top = input.weakCategories[0];
    const focusArea = top?.label ?? 'steady recall';
    const diagnosis = top
      ? `${subject} answered ${input.score} of ${input.questionCount} correctly, which is ${percent}%. ${top.label} slowed things down today, especially on ${top.examples.slice(0, 2).join(' and ')}. A short follow-up on those facts should make the next run feel smoother.`
      : `${subject} answered ${input.score} of ${input.questionCount} correctly, which is ${percent}%. The pace looked steady, so the follow-up will keep recall warm across mixed facts. Keep the practice short and calm.`;

    return { diagnosis, focusArea };
  }

  async explain(input: ExplanationInput): Promise<ExplanationResult> {
    const answer = input.operator === '+' ? input.operand1 + input.operand2 : input.operand1 * input.operand2;
    const question = questionText({ ...input, operator: input.operator });
    const explanation = input.operator === '+'
      ? `Start with ${input.operand1}, then count on ${input.operand2} more to land on ${answer}.`
      : `Think of ${question} as ${input.operand1} groups of ${input.operand2}. That makes ${answer} altogether.`;
    return { question, answer, explanation };
  }
}
